"use strict";
/**
 * Tenant branding configuration.
 *
 * Maps tenant UUIDs (organization.tenantId) to display metadata used by the
 * client-side PDF export.  Sourced from tenant-uuids.csv (definitive list as
 * of 2026-05-17).
 *
 * To onboard a new tenant: add the JPG to ../assets/logos/, import it below,
 * then add the TENANT_BRANDING entry keyed by the tenant UUID.
 *
 * Tenants without a confirmed logo file are listed with logo: undefined; the
 * print helper renders a text-only header in that case.
 *
 * Academic calendar and course description links are organization-scoped (not
 * tenant-scoped) and are returned by the /organization API once the platform
 * stores them.  They are not managed here.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TENANT_BRANDING = void 0;
exports.getTenantBranding = getTenantBranding;
const CSUMB_jpg_1 = __importDefault(require("../assets/logos/CSUMB.jpg"));
const CWRU_jpg_1 = __importDefault(require("../assets/logos/CWRU.jpg"));
const DIGIPEN_jpg_1 = __importDefault(require("../assets/logos/DIGIPEN.jpg"));
const HOFSTRA_jpg_1 = __importDefault(require("../assets/logos/HOFSTRA.jpg"));
const KSU_jpg_1 = __importDefault(require("../assets/logos/KSU.jpg"));
const KU_jpg_1 = __importDefault(require("../assets/logos/KU.jpg"));
const MARQUETTE_jpg_1 = __importDefault(require("../assets/logos/MARQUETTE.jpg"));
const NU_jpg_1 = __importDefault(require("../assets/logos/NU.jpg"));
const NYIT_jpg_1 = __importDefault(require("../assets/logos/NYIT.jpg"));
const OKCU_jpg_1 = __importDefault(require("../assets/logos/OKCU.jpg"));
const PITT_jpg_1 = __importDefault(require("../assets/logos/PITT.jpg"));
const SCU_jpg_1 = __importDefault(require("../assets/logos/SCU.jpg"));
const SEATTLE_jpg_1 = __importDefault(require("../assets/logos/SEATTLE.jpg"));
const SMU_jpg_1 = __importDefault(require("../assets/logos/SMU.jpg"));
const TULANE_jpg_1 = __importDefault(require("../assets/logos/TULANE.jpg"));
const WM_jpg_1 = __importDefault(require("../assets/logos/WM.jpg"));
const YU_jpg_1 = __importDefault(require("../assets/logos/YU.jpg"));
function logo(src, displayName) {
    return { src, alt: `${displayName} logo` };
}
exports.TENANT_BRANDING = {
    '92a9be7c-6b69-4066-b131-b503b2c4838d': {
        tenantId: '92a9be7c-6b69-4066-b131-b503b2c4838d',
        code: 'KU',
        displayName: 'The University of Kansas',
        logo: logo(KU_jpg_1.default, 'The University of Kansas'),
    },
    'e8f56d1e-22e4-4960-9532-f031c2a4258e': {
        tenantId: 'e8f56d1e-22e4-4960-9532-f031c2a4258e',
        code: 'KSU',
        displayName: 'Kent State University',
        logo: logo(KSU_jpg_1.default, 'Kent State University'),
    },
    'c27e4f70-81dd-4036-a4af-f0f93c6b3286': {
        tenantId: 'c27e4f70-81dd-4036-a4af-f0f93c6b3286',
        code: 'WM',
        displayName: 'College of William & Mary',
        logo: logo(WM_jpg_1.default, 'College of William & Mary'),
    },
    '835b1dfd-d563-4006-873e-14819cd7d6e8': {
        tenantId: '835b1dfd-d563-4006-873e-14819cd7d6e8',
        code: 'CSUMB',
        displayName: 'California State University at Monterey Bay',
        logo: logo(CSUMB_jpg_1.default, 'California State University at Monterey Bay'),
    },
    'a5998b86-b890-4127-8ec0-49797dd091f7': {
        tenantId: 'a5998b86-b890-4127-8ec0-49797dd091f7',
        code: 'CWRU',
        displayName: 'Case Western Reserve University',
        logo: logo(CWRU_jpg_1.default, 'Case Western Reserve University'),
    },
    '67da7acf-3f4e-4e9a-85d5-f6210cac527e': {
        tenantId: '67da7acf-3f4e-4e9a-85d5-f6210cac527e',
        code: 'TULANE',
        displayName: 'Tulane University',
        logo: logo(TULANE_jpg_1.default, 'Tulane University'),
    },
    '6ee0a15f-da90-4433-a91c-b661af3f3a24': {
        tenantId: '6ee0a15f-da90-4433-a91c-b661af3f3a24',
        code: 'SCU',
        displayName: 'Santa Clara University',
        logo: logo(SCU_jpg_1.default, 'Santa Clara University'),
    },
    'b75fd532-9fcc-420a-9772-bfe4c2b4b2b8': {
        tenantId: 'b75fd532-9fcc-420a-9772-bfe4c2b4b2b8',
        code: 'YU',
        displayName: 'Yeshiva University',
        logo: logo(YU_jpg_1.default, 'Yeshiva University'),
    },
    'a07f0a3e-3096-4fda-bf74-dd1cfdb28cd3': {
        tenantId: 'a07f0a3e-3096-4fda-bf74-dd1cfdb28cd3',
        code: 'SEATTLE',
        displayName: 'Seattle University',
        logo: logo(SEATTLE_jpg_1.default, 'Seattle University'),
    },
    '7d70fcc4-8a51-4768-a00d-e884cee2bdb5': {
        tenantId: '7d70fcc4-8a51-4768-a00d-e884cee2bdb5',
        code: 'MARQUETTE',
        displayName: 'Marquette University',
        logo: logo(MARQUETTE_jpg_1.default, 'Marquette University'),
    },
    '106bee39-9360-4b35-9415-2cc2b604322f': {
        tenantId: '106bee39-9360-4b35-9415-2cc2b604322f',
        code: 'NU',
        displayName: 'Northwestern University',
        logo: logo(NU_jpg_1.default, 'Northwestern University'),
    },
    '6c8f545d-f3ce-45ce-af68-e4726e456f82': {
        tenantId: '6c8f545d-f3ce-45ce-af68-e4726e456f82',
        code: 'PITT',
        displayName: 'University of Pittsburgh',
        logo: logo(PITT_jpg_1.default, 'University of Pittsburgh'),
    },
    '70a11cdc-8199-4356-8062-052e3ce57df8': {
        tenantId: '70a11cdc-8199-4356-8062-052e3ce57df8',
        code: 'OKCU',
        displayName: 'Oklahoma City University',
        logo: logo(OKCU_jpg_1.default, 'Oklahoma City University'),
    },
    'cb3f4254-7960-4afa-9c3a-11e2615e9895': {
        tenantId: 'cb3f4254-7960-4afa-9c3a-11e2615e9895',
        code: 'NYIT',
        displayName: 'New York Institute of Technology',
        logo: logo(NYIT_jpg_1.default, 'New York Institute of Technology'),
    },
    // Logos pending — text-only header until JPGs are supplied.
    '6a6cfb40-8eb5-47b0-a5e5-27dbede40a2e': {
        tenantId: '6a6cfb40-8eb5-47b0-a5e5-27dbede40a2e',
        code: 'IOWA',
        displayName: 'University of Iowa',
    },
    '6f2caee0-cdf7-4b69-a6df-c31128a0a9db': {
        tenantId: '6f2caee0-cdf7-4b69-a6df-c31128a0a9db',
        code: 'MSOE',
        displayName: 'Milwaukee School of Engineering',
    },
    'db6859ce-f5b1-41da-81f0-d7774553b213': {
        tenantId: 'db6859ce-f5b1-41da-81f0-d7774553b213',
        code: 'SMU',
        displayName: 'Southern Methodist University',
        logo: logo(SMU_jpg_1.default, 'Southern Methodist University'),
    },
    '4a8aa050-8d27-435b-aa84-86f2d2ac6c39': {
        tenantId: '4a8aa050-8d27-435b-aa84-86f2d2ac6c39',
        code: 'HOFSTRA',
        displayName: 'Hofstra University',
        logo: logo(HOFSTRA_jpg_1.default, 'Hofstra University'),
    },
    'a78d1b55-ef57-4ef6-b67a-61068cc57d66': {
        tenantId: 'a78d1b55-ef57-4ef6-b67a-61068cc57d66',
        code: 'DIGIPEN',
        displayName: 'DigiPen Institute of Technology',
        logo: logo(DIGIPEN_jpg_1.default, 'DigiPen Institute of Technology'),
    },
};
/**
 * Resolves branding for a given tenant. Returns undefined when the tenant is
 * unmapped so callers can render a graceful fallback (org.name only, no logo).
 */
function getTenantBranding(tenantId) {
    if (!tenantId)
        return undefined;
    return Object.prototype.hasOwnProperty.call(exports.TENANT_BRANDING, tenantId)
        // eslint-disable-next-line security/detect-object-injection
        ? exports.TENANT_BRANDING[tenantId]
        : undefined;
}
